import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type {
  BulkSocialPostsRequest,
  CreateSocialPostRequest,
  PublishSocialPostRequest,
  SocialPost,
  UpdateSocialPostRequest,
} from '@nucleus-commerce/api-client/admin';

import { socialPostsApi } from '../api/social-posts-api';

import { socialKeys } from './social-keys';

function remember(queryClient: QueryClient, storeId: string, row: SocialPost) {
  queryClient.setQueryData(socialKeys.post(storeId, row.id), row);
  return queryClient.invalidateQueries({ queryKey: socialKeys.postLists(storeId) });
}

/** Field errors (text limits, unknown product or image) are rendered by the form, not toasted. */
export function useCreateSocialPost(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSocialPostRequest) => socialPostsApi.create(storeId, body),
    meta: { successMessage: 'social:toasts.postCreated', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useUpdateSocialPost(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateSocialPostRequest }) =>
      socialPostsApi.update(storeId, id, body),
    meta: { successMessage: 'social:toasts.postSaved', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

/**
 * Publish now (no `publishAt`), schedule, reschedule and retry are all this call; the toast is
 * chosen by the caller because the same hook serves all four.
 */
export function usePublishSocialPost(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PublishSocialPostRequest }) =>
      socialPostsApi.publish(storeId, id, body),
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useUnscheduleSocialPost(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => socialPostsApi.unschedule(storeId, id),
    meta: { successMessage: 'social:toasts.postUnscheduled' },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useRemoveSocialPost(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, removeFromProvider }: { id: string; removeFromProvider: boolean }) =>
      socialPostsApi.remove(storeId, id, removeFromProvider),
    meta: { successMessage: 'social:toasts.postRemoved', silentError: true },
    onSuccess: (_void, { id }) => {
      queryClient.removeQueries({ queryKey: socialKeys.post(storeId, id) });
      return queryClient.invalidateQueries({ queryKey: socialKeys.postLists(storeId) });
    },
  });
}

/** One outcome per id; the request answers 200 whatever happened, so the page reads the results. */
export function useBulkSocialPosts(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkSocialPostsRequest) => socialPostsApi.bulk(storeId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: socialKeys.posts(storeId) }),
  });
}
